import React, { useState, useEffect, useCallback } from 'react';
import { invItemService, invCategoryService, invUnitService, invSupplierService } from '../../services/inventoryService';
import { card, pageWrap, btn, inputStyle, selectStyle, label, fieldGroup, modal, modalBox,
  statusBadge, sectionTitle, errorBox, emptyState, fmtQty, fmtCost, friendlyErr } from './inventoryStyles';

const ITEM_TYPES = [
  { value: 'raw_material',    label: 'Raw Material' },
  { value: 'packing_material',label: 'Packing Material' },
  { value: 'finished_good',   label: 'Finished Good' },
  { value: 'semi_finished',   label: 'Semi-Finished' },
];

const EMPTY_FORM = {
  name: '', item_code: '', item_type: 'raw_material', category_id: '',
  base_unit_id: '', purchase_unit_id: '', conversion_factor: '1',
  min_stock_threshold: '0', preferred_supplier_id: '',
  is_active: true, notes: '',
};

const EMPTY_OPENING = { qty: '', value: '' };

const StockItems = ({ restaurant }) => {
  const [items, setItems]         = useState([]);
  const [cats, setCats]           = useState([]);
  const [units, setUnits]         = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [filterStatus, setFilterStatus] = useState('active');
  const [showForm, setShowForm]   = useState(false);
  const [editing, setEditing]     = useState(null);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [openingForm, setOpeningForm] = useState(null); // itemId if setting opening stock
  const [openingData, setOpeningData] = useState(EMPTY_OPENING);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const restaurantId = restaurant?.id;

  const load = useCallback(async () => {
    setLoading(true);
    const [iRes, cRes, uRes, sRes] = await Promise.all([
      invItemService.getAll(restaurantId),
      invCategoryService.getAll(restaurantId),
      invUnitService.getAll(restaurantId),
      invSupplierService.getAll(restaurantId),
    ]);
    setItems(iRes.data || []);
    setCats(cRes.data || []);
    setUnits(uRes.data || []);
    setSuppliers(sRes.data || []);
    setLoading(false);
  }, [restaurantId]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(EMPTY_FORM); setError(''); setShowForm(true); };
  const openEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name, item_code: item.item_code || '', item_type: item.item_type,
      category_id: item.category_id || '', base_unit_id: item.base_unit_id || '',
      purchase_unit_id: item.purchase_unit_id || '', conversion_factor: String(item.conversion_factor || 1),
      min_stock_threshold: String(item.min_stock_threshold || 0),
      preferred_supplier_id: item.preferred_supplier_id || '',
      is_active: item.is_active, notes: item.notes || '',
    });
    setError('');
    setShowForm(true);
  };

  const save = async () => {
    if (!form.name.trim()) { setError('Item name is required.'); return; }
    if (!form.base_unit_id) { setError('Base unit is required.'); return; }
    setSaving(true); setError('');
    const payload = { ...form, id: editing?.id };
    const { error: e } = await invItemService.upsert(restaurantId, payload);
    setSaving(false);
    if (e) { setError(friendlyErr(e)); return; }
    setShowForm(false);
    load();
  };

  const toggleActive = async (item) => {
    await invItemService.setActive(item.id, !item.is_active);
    load();
  };

  const saveOpening = async () => {
    if (!openingData.qty || parseFloat(openingData.qty) < 0) { setError('Valid quantity required.'); return; }
    setSaving(true); setError('');
    const { error: e } = await invItemService.setOpeningStock(
      restaurantId, openingForm,
      parseFloat(openingData.qty), parseFloat(openingData.value) || 0
    );
    setSaving(false);
    if (e) { setError(friendlyErr(e)); return; }
    setOpeningForm(null); setOpeningData(EMPTY_OPENING);
    load();
  };

  const filtered = items.filter(i => {
    const matchSearch = i.name.toLowerCase().includes(search.toLowerCase()) ||
                        (i.item_code || '').toLowerCase().includes(search.toLowerCase());
    const matchCat    = !filterCat || i.category_id === filterCat;
    const matchStatus = filterStatus === 'all' ? true :
                        filterStatus === 'active' ? i.is_active :
                        filterStatus === 'low_stock' ? (i.current_stock > 0 && i.current_stock <= (i.min_stock_threshold || 0)) :
                        filterStatus === 'out_of_stock' ? i.current_stock <= 0 : true;
    return matchSearch && matchCat && matchStatus;
  });

  const getStockStatus = (item) => {
    if (item.current_stock <= 0) return 'out_of_stock';
    if (item.current_stock <= (item.min_stock_threshold || 0)) return 'low_stock';
    return 'ok';
  };

  return (
    <div style={pageWrap}>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '800', color: '#111' }}>Stock Items</h2>
            <p style={{ margin: '4px 0 0', color: '#888', fontSize: '0.88rem' }}>{items.filter(i => i.is_active).length} active items · {fmtCost(items.reduce((s, i) => s + i.current_stock * (i.avg_unit_cost || 0), 0))} total value</p>
          </div>
          <button style={btn('primary')} onClick={openNew}>+ Add Item</button>
        </div>

        {/* Filters */}
        <div style={{ ...card, padding: '16px 20px', marginBottom: '20px', display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            placeholder="Search items…" value={search} onChange={e => setSearch(e.target.value)}
            style={{ ...inputStyle(), width: '220px', padding: '8px 12px' }}
          />
          <select value={filterCat} onChange={e => setFilterCat(e.target.value)} style={{ ...selectStyle(), width: '160px', padding: '8px 12px' }}>
            <option value="">All Categories</option>
            {cats.filter(c => c.is_active).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...selectStyle(), width: '160px', padding: '8px 12px' }}>
            <option value="active">Active</option>
            <option value="all">All</option>
            <option value="low_stock">Low Stock</option>
            <option value="out_of_stock">Out of Stock</option>
          </select>
          <span style={{ marginLeft: 'auto', fontSize: '0.85rem', color: '#888' }}>{filtered.length} items</span>
        </div>

        {/* Table */}
        <div style={{ ...card, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ ...emptyState }}>Loading items…</div>
          ) : filtered.length === 0 ? (
            <div style={emptyState}>
              <div style={{ fontWeight: '600', color: '#555', marginBottom: '8px' }}>No stock items found</div>
              <div>Add your first item to get started.</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                <thead>
                  <tr style={{ background: '#f9fafb', borderBottom: '2px solid #f3f4f6' }}>
                    {['Item', 'Category', 'Type', 'Stock', 'Reorder Level', 'Avg Cost', 'Stock Value', 'Status', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontWeight: '700', color: '#555', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(item => {
                    const stockStatus = getStockStatus(item);
                    return (
                      <tr key={item.id} style={{ borderBottom: '1px solid #f3f4f6', opacity: item.is_active ? 1 : 0.5 }}>
                        <td style={{ padding: '12px 14px' }}>
                          <div style={{ fontWeight: '700', color: '#333' }}>{item.name}</div>
                          {item.item_code && <div style={{ fontSize: '0.75rem', color: '#888' }}>{item.item_code}</div>}
                        </td>
                        <td style={{ padding: '12px 14px', color: '#555' }}>{item.category?.name || '—'}</td>
                        <td style={{ padding: '12px 14px', color: '#555' }}>{ITEM_TYPES.find(t => t.value === item.item_type)?.label || item.item_type}</td>
                        <td style={{ padding: '12px 14px' }}>
                          <span style={{ fontWeight: '700', color: stockStatus === 'out_of_stock' ? '#dc2626' : stockStatus === 'low_stock' ? '#c2410c' : '#333' }}>
                            {fmtQty(item.current_stock)} {item.base_unit?.short_code || ''}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px', color: '#555' }}>{fmtQty(item.min_stock_threshold)} {item.base_unit?.short_code || ''}</td>
                        <td style={{ padding: '12px 14px', color: '#555' }}>{fmtCost(item.avg_unit_cost)}/{item.base_unit?.short_code || 'unit'}</td>
                        <td style={{ padding: '12px 14px', fontWeight: '600', color: '#16a34a' }}>{fmtCost(item.current_stock * (item.avg_unit_cost || 0))}</td>
                        <td style={{ padding: '12px 14px' }}>
                          <span style={statusBadge(stockStatus)}>
                            {stockStatus === 'out_of_stock' ? 'Out of Stock' : stockStatus === 'low_stock' ? 'Low Stock' : 'In Stock'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'nowrap' }}>
                            <button style={{ ...btn('outline'), padding: '5px 10px', fontSize: '0.78rem' }} onClick={() => openEdit(item)}>Edit</button>
                            {item.current_stock === 0 && (
                              <button style={{ ...btn('green'), padding: '5px 10px', fontSize: '0.78rem' }} onClick={() => { setOpeningForm(item.id); setOpeningData(EMPTY_OPENING); }}>
                                Set Opening
                              </button>
                            )}
                            <button
                              style={{ ...btn(item.is_active ? 'danger' : 'green'), padding: '5px 10px', fontSize: '0.78rem' }}
                              onClick={() => toggleActive(item)}
                            >
                              {item.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Item Form Modal */}
      {showForm && (
        <div style={modal} onClick={e => { if (e.target === e.currentTarget) setShowForm(false); }}>
          <div style={modalBox}>
            <h3 style={{ ...sectionTitle, marginBottom: '20px' }}>{editing ? 'Edit Item' : 'New Stock Item'}</h3>
            {error && <div style={errorBox}>{error}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
              <div style={{ ...fieldGroup, gridColumn: '1 / -1' }}>
                <label style={label}>Item Name *</label>
                <input style={inputStyle(!form.name.trim())} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Tomatoes" />
              </div>
              <div style={fieldGroup}>
                <label style={label}>Item Code</label>
                <input style={inputStyle()} value={form.item_code} onChange={e => setForm(f => ({ ...f, item_code: e.target.value }))} placeholder="e.g. TOM-001" />
              </div>
              <div style={fieldGroup}>
                <label style={label}>Item Type</label>
                <select style={selectStyle()} value={form.item_type} onChange={e => setForm(f => ({ ...f, item_type: e.target.value }))}>
                  {ITEM_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div style={fieldGroup}>
                <label style={label}>Category</label>
                <select style={selectStyle()} value={form.category_id} onChange={e => setForm(f => ({ ...f, category_id: e.target.value }))}>
                  <option value="">No Category</option>
                  {cats.filter(c => c.is_active).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div style={fieldGroup}>
                <label style={label}>Base Unit *</label>
                <select style={selectStyle(!form.base_unit_id)} value={form.base_unit_id} onChange={e => setForm(f => ({ ...f, base_unit_id: e.target.value }))}>
                  <option value="">Select unit…</option>
                  {units.map(u => <option key={u.id} value={u.id}>{u.name} ({u.short_code})</option>)}
                </select>
              </div>
              <div style={fieldGroup}>
                <label style={label}>Purchase Unit</label>
                <select style={selectStyle()} value={form.purchase_unit_id} onChange={e => setForm(f => ({ ...f, purchase_unit_id: e.target.value }))}>
                  <option value="">Same as base</option>
                  {units.map(u => <option key={u.id} value={u.id}>{u.name} ({u.short_code})</option>)}
                </select>
              </div>
              <div style={fieldGroup}>
                <label style={label}>Conversion Factor</label>
                <input style={inputStyle()} type="number" min="0.0001" step="0.001" value={form.conversion_factor} onChange={e => setForm(f => ({ ...f, conversion_factor: e.target.value }))} />
                <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '4px' }}>How many base units = 1 purchase unit</div>
              </div>
              <div style={fieldGroup}>
                <label style={label}>Reorder Level</label>
                <input style={inputStyle()} type="number" min="0" step="0.001" value={form.min_stock_threshold} onChange={e => setForm(f => ({ ...f, min_stock_threshold: e.target.value }))} placeholder="0" />
                <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '4px' }}>Stock turns Low when it falls at or below this</div>
              </div>
              <div style={{ ...fieldGroup, gridColumn: '1 / -1' }}>
                <label style={label}>Preferred Supplier</label>
                <select style={selectStyle()} value={form.preferred_supplier_id} onChange={e => setForm(f => ({ ...f, preferred_supplier_id: e.target.value }))}>
                  <option value="">No preference</option>
                  {suppliers.filter(s => s.is_active).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div style={{ ...fieldGroup, gridColumn: '1 / -1' }}>
                <label style={label}>Notes</label>
                <textarea style={{ ...inputStyle(), height: '72px', resize: 'vertical' }} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional notes…" />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px' }}>
              <button style={btn('outline')} onClick={() => setShowForm(false)}>Cancel</button>
              <button style={btn('primary', saving)} onClick={save} disabled={saving}>{saving ? 'Saving…' : editing ? 'Save Changes' : 'Add Item'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Opening Stock Modal */}
      {openingForm && (
        <div style={modal} onClick={e => { if (e.target === e.currentTarget) setOpeningForm(null); }}>
          <div style={{ ...modalBox, maxWidth: '400px' }}>
            <h3 style={{ ...sectionTitle, marginBottom: '16px' }}>Set Opening Stock</h3>
            <p style={{ color: '#666', fontSize: '0.88rem', marginBottom: '20px' }}>
              Set the current physical stock for this item. This will create an opening stock ledger entry.
            </p>
            {error && <div style={errorBox}>{error}</div>}
            <div style={fieldGroup}>
              <label style={label}>Opening Quantity *</label>
              <input style={inputStyle(!openingData.qty)} type="number" min="0" step="0.001" value={openingData.qty} onChange={e => setOpeningData(d => ({ ...d, qty: e.target.value }))} placeholder="0.000" />
            </div>
            <div style={fieldGroup}>
              <label style={label}>Total Value (₹)</label>
              <input style={inputStyle()} type="number" min="0" step="0.01" value={openingData.value} onChange={e => setOpeningData(d => ({ ...d, value: e.target.value }))} placeholder="0.00" />
              <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '4px' }}>Used to calculate average unit cost</div>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button style={btn('outline')} onClick={() => setOpeningForm(null)}>Cancel</button>
              <button style={btn('primary', saving)} onClick={saveOpening} disabled={saving}>{saving ? 'Saving…' : 'Set Opening Stock'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockItems;
